import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Bell,
  CheckCheck,
  Clock,
  ExternalLink,
  Inbox,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { notificationAPI } from '../lib/api';
import { notificationLink } from '../lib/notificationLinks';
import { handleNotificationOpen, isNotificationUnread } from '../lib/notificationOpen';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';

const TYPE_LABELS = {
  BOOKING: 'Booking',
  PROPERTY_ALERT: 'Property Alert',
  REVIEW_APPROVED: 'Review Approved',
  REVIEW_REJECTED: 'Review Rejected',
  REVIEW_SUBMITTED: 'Review Submitted',
  LISTING_SUBMITTED: 'Listing Submitted',
  LISTING_APPROVED: 'Listing Approved',
  LISTING_REJECTED: 'Listing Rejected',
  LISTING_RESUBMITTED: 'Listing Resubmitted',
  WALKTHROUGH_REQUEST: 'Walkthrough',
  SCHEDULER: 'Scheduler',
  SYSTEM: 'System',
};

function isUnread(notification) {
  return isNotificationUnread(notification);
}

function formatTimeAgo(dateString) {
  if (!dateString) return '';
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (Number.isNaN(seconds)) return '';
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return date.toLocaleDateString();
}

export default function NotificationsPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [actingOn, setActingOn] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/notifications');
    }
  }, [authLoading, user, navigate]);

  const loadNotifications = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await notificationAPI.getAll({ limit: 50 });
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch (error) {
      console.error('Failed to load notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications();
  }, [user]);

  const filteredNotifications = useMemo(() => {
    if (filter === 'unread') return notifications.filter(isUnread);
    return notifications;
  }, [notifications, filter]);

  const markAsRead = async (notificationId) => {
    setActingOn(notificationId);
    try {
      await notificationAPI.markRead(notificationId);
      setNotifications((prev) =>
        prev.map((item) =>
          item.id === notificationId ? { ...item, is_read: true } : item
        )
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Failed to mark notification read:', error);
    } finally {
      setActingOn(null);
    }
  };

  const markAllAsRead = async () => {
    setActingOn('all');
    try {
      await notificationAPI.markAllRead();
      setNotifications((prev) => prev.map((item) => ({ ...item, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Failed to mark all notifications read:', error);
    } finally {
      setActingOn(null);
    }
  };

  const deleteNotification = async (notificationId, e) => {
    e?.stopPropagation?.();
    setActingOn(notificationId);
    try {
      const existing = notifications.find((item) => item.id === notificationId);
      await notificationAPI.delete(notificationId);
      setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
      if (existing && isUnread(existing)) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Failed to delete notification:', error);
    } finally {
      setActingOn(null);
    }
  };

  const onNotificationOpen = (notification) => {
    handleNotificationOpen({
      notification,
      user,
      navigate,
      markAsRead: (id) => markAsRead(id),
    });
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-100 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-signal-blue animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 pt-20" data-testid="notifications-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 rounded-xl bg-signal-blue/10 flex items-center justify-center">
                <Inbox className="w-6 h-6 text-signal-blue" />
              </div>
              <div>
                <h1 className="font-heading text-3xl font-bold text-slate-800">Notification Inbox</h1>
                <p className="text-sm text-slate-600">
                  Track listing reviews, booking requests, scheduler updates, and platform activity.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={loadNotifications}
              className="border-slate-300 text-slate-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
            <Button
              onClick={markAllAsRead}
              disabled={unreadCount === 0 || actingOn === 'all'}
              className="bg-signal-blue hover:bg-signal-blue-hover text-white"
            >
              {actingOn === 'all' ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-2" />}
              Mark all read
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="label-tactical">Total</p>
            <p className="text-2xl font-bold text-slate-800">{notifications.length}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="label-tactical">Unread</p>
            <p className="text-2xl font-bold text-signal-blue">{unreadCount}</p>
          </div>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <p className="label-tactical">Mode</p>
            <p className="text-2xl font-bold text-slate-800">In-app</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between gap-3 px-4 py-3 border-b border-slate-200 bg-slate-50">
            <div className="flex gap-2">
              <Button
                size="sm"
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-signal-blue text-white' : 'border-slate-300 text-slate-700'}
              >
                All
              </Button>
              <Button
                size="sm"
                variant={filter === 'unread' ? 'default' : 'outline'}
                onClick={() => setFilter('unread')}
                className={filter === 'unread' ? 'bg-signal-blue text-white' : 'border-slate-300 text-slate-700'}
              >
                Unread
              </Button>
            </div>
            <p className="text-xs text-slate-500">Showing latest 50 notifications</p>
          </div>

          {filteredNotifications.length === 0 ? (
            <div className="py-16 px-4 text-center">
              <Bell className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <h3 className="font-heading text-xl font-bold text-slate-800">No notifications here</h3>
              <p className="text-sm text-slate-500 mt-1">
                New listing reviews, booking requests, scheduler updates, and admin actions will appear here.
              </p>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {filteredNotifications.map((notification) => {
                const unread = isUnread(notification);
                const link = notificationLink(notification, user);
                const openLabel = link
                  ? 'Open notification'
                  : unread
                    ? 'Mark as read'
                    : undefined;

                return (
                  <div
                    key={notification.id}
                    role={link ? 'button' : undefined}
                    tabIndex={link ? 0 : undefined}
                    aria-label={openLabel}
                    title={openLabel}
                    className={`p-4 transition-colors ${
                      link ? 'cursor-pointer hover:bg-slate-100' : unread ? 'cursor-pointer hover:bg-slate-50' : ''
                    } ${unread ? 'bg-blue-50/50' : 'bg-white'}`}
                    data-testid={`notification-row-${notification.id}`}
                    onClick={() => onNotificationOpen(notification)}
                    onKeyDown={(e) => {
                      if (link && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onNotificationOpen(notification);
                      }
                    }}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2.5 h-2.5 rounded-full mt-2 ${unread ? 'bg-signal-blue' : 'bg-slate-200'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-800">{notification.title || 'Notification'}</h3>
                          <Badge className="bg-slate-100 text-slate-700 border border-slate-200 text-xs">
                            {TYPE_LABELS[notification.type] || notification.type || 'General'}
                          </Badge>
                          {unread && (
                            <Badge className="bg-signal-blue/10 text-signal-blue border border-signal-blue/20 text-xs">
                              New
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">{notification.message}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-3 text-xs text-slate-500">
                          <span className="inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatTimeAgo(notification.created_at)}
                          </span>
                          {link && (
                            <Link
                              to={link}
                              onClick={(e) => {
                                e.stopPropagation();
                                onNotificationOpen(notification);
                              }}
                              className="inline-flex items-center gap-1 text-signal-blue hover:underline"
                            >
                              Open related item <ExternalLink className="w-3 h-3" />
                            </Link>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-2 flex-shrink-0">
                        {unread && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => markAsRead(notification.id)}
                            disabled={actingOn === notification.id}
                            className="border-slate-300 text-slate-700"
                          >
                            {actingOn === notification.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Read'}
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => deleteNotification(notification.id, e)}
                          disabled={actingOn === notification.id}
                          className="text-red-500 hover:text-red-600 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
